import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Trash2, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UOM_OPTIONS } from "@/constants/uom";
import SearchableSelect from "@/components/SearchableSelect";

const emptyItem = () => ({ description: "", qty: 1, uom: "each", unit_price: 0, cost_code_id: "" });

export default function RFQForm() {
  const [jobs, setJobs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [jobId, setJobId] = useState("");
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    api.get("/contractor/vendor-network").then((r) => setVendors(r.data));
    api.get("/contractor/cost-codes").then((r) => setCostCodes(r.data));
  }, []);

  const updateItem = (i, field, value) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: field === "qty" || field === "unit_price" ? Number(value) : value };
    setItems(next);
  };
  const addItem = () => setItems([...items, emptyItem()]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!jobId || !title || selectedVendors.length === 0) return toast.error("Job, title and at least one vendor are required");
    const lineItems = items.filter((i) => i.description);
    if (lineItems.length === 0) return toast.error("At least one line item is required");
    if (lineItems.some((i) => !i.cost_code_id)) return toast.error("Every line item needs a cost code");
    setSaving(true);
    try {
      const { data } = await api.post("/contractor/rfqs", {
        job_id: jobId, title, vendor_ids: selectedVendors, due_date: dueDate || null,
        items: lineItems.map((i) => ({ description: i.description, qty: i.qty, uom: i.uom, unit_price: i.unit_price, cost_code_id: i.cost_code_id })),
      });
      toast.success("RFQ created and emailed to vendors");
      navigate(`/contractor/rfqs/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message || d).join(" ") : detail || "Failed to create RFQ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="new-rfq-page">
      <Link to="/contractor/purchase-orders" data-testid="back-link" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to Purchase Orders</Link>
      <h1 className="text-2xl font-extrabold tracking-tight">New RFQ</h1>
      <p className="text-slate-500 text-sm mt-1">Send a request for quote to selected vendors by email.</p>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[13px] font-medium text-slate-700">Job *</label>
          <SearchableSelect value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="rfq-job-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
            <option value="">Select job…</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
          </SearchableSelect>
        </div>
        <div>
          <label className="text-[13px] font-medium text-slate-700">Due Date</label>
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="rfq-due-date-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[13px] font-medium text-slate-700">Title *</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="rfq-title-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" placeholder="e.g. Landscape supply for Riverside Apartments" />
        </div>
      </div>

      <div className="mt-6">
        <label className="text-[13px] font-semibold text-slate-700">Send to Vendors *</label>
        <p className="text-[11.5px] text-slate-400 mt-0.5">Each selected vendor's contact email gets the RFQ details directly.</p>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {vendors.map((v) => (
            <label key={v.vendor_id} className="flex items-center gap-2 text-[13px] border border-slate-200 rounded-lg px-3 py-2">
              <input type="checkbox" checked={selectedVendors.includes(v.vendor_id)} data-testid={`vendor-checkbox-${v.vendor_id}`} onChange={(e) => setSelectedVendors(e.target.checked ? [...selectedVendors, v.vendor_id] : selectedVendors.filter((x) => x !== v.vendor_id))} />
              {v.vendor_name}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-7">
        <div className="flex items-center justify-between">
          <label className="text-[13px] font-semibold text-slate-700">Line Items</label>
          <button onClick={addItem} data-testid="rfq-add-item-button" className="text-[12.5px] font-semibold text-sky-600 flex items-center gap-1"><Plus size={13} /> Add item</button>
        </div>
        <div className="mt-2 space-y-2">
          {items.map((item, i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`rfq-item-row-${i}`}>
              <div className="grid grid-cols-12 gap-2 items-center">
                <input className="col-span-6 h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" placeholder="Description" value={item.description} onChange={(e) => updateItem(i, "description", e.target.value)} data-testid={`rfq-item-desc-${i}`} />
                <input type="number" min="0" className="col-span-2 h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" placeholder="Qty" value={item.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} data-testid={`rfq-item-qty-${i}`} />
                <SearchableSelect className="col-span-3 h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]" value={item.uom} onChange={(e) => updateItem(i, "uom", e.target.value)} data-testid={`rfq-item-uom-${i}`}>
                  {UOM_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </SearchableSelect>
                <button onClick={() => removeItem(i)} className="col-span-1 text-slate-400 hover:text-red-500 flex items-center justify-center"><Trash2 size={15} /></button>
              </div>
              <SearchableSelect className="w-full h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]" value={item.cost_code_id} onChange={(e) => updateItem(i, "cost_code_id", e.target.value)} data-testid={`rfq-item-cost-code-${i}`}>
                <option value="">Cost code…</option>
                {costCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
              </SearchableSelect>
            </div>
          ))}
        </div>
      </div>

      <button disabled={saving} onClick={submit} data-testid="create-rfq-button" className="mt-7 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60">
        {saving ? "Creating…" : "Create & Send RFQ"}
      </button>
    </div>
  );
}
